/**
 * ComplianceRiskAlert
 *
 * Admin/nurse-facing card that surfaces aides projected to miss their YTD
 * in-service target. Backend endpoint computes a proportional pace line —
 * if Oct 1 (75% of year) and the aide has only 4/12 hours, that's a 5h gap
 * and they show up here in red.
 *
 * Severity buckets:
 *   red   = gap > 25% of target
 *   amber = gap 10-25% of target
 *   green = gap < 10%  (we filter these out — they're fine)
 *
 * Used on:
 *   • Practice Admin / Super Admin dashboards (full card)
 *   • Inline above HR list for a quick admin nudge (compact mode)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, ChevronRight, ShieldAlert, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const SEVERITY_STYLES = {
  red: 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]',
  amber: 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]',
};

export default function ComplianceRiskAlert({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/inservice/compliance/aides-at-risk`,
          { headers: authHeader() });
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData({ aides: [], at_risk_count: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="border-[#E2E8F0]" data-testid="compliance-risk-loading">
        <CardContent className="p-4 text-xs text-[#64748B] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking in-service compliance…
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.at_risk_count) {
    return compact ? null : (
      <Card className="border-emerald-200 bg-emerald-50" data-testid="compliance-risk-clear">
        <CardContent className="p-4 flex items-center gap-3 text-sm text-emerald-900">
          <ShieldAlert className="w-5 h-5 text-emerald-700" />
          <div>
            <div className="font-semibold">All aides on pace for in-service hours</div>
            <div className="text-xs text-emerald-800/80">
              Year is {data?.year_progress_percent || 0}% complete · 0 at-risk aides.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const top = data.aides.slice(0, compact ? 3 : 8);
  const more = data.aides.length - top.length;

  return (
    <Card className="border-[#FCA5A5]" data-testid="compliance-risk-alert">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-[#991B1B] flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          In-service compliance risk
          <Badge className="ml-1 bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5]">
            {data.at_risk_count}
          </Badge>
        </CardTitle>
        <span className="text-[10px] text-[#64748B]">
          Year {data.year_progress_percent}% complete
        </span>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {top.map(a => (
          <Link key={a.user_id} to={`/hr/${a.user_id}`}
                className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border text-xs hover:opacity-90 ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.amber}`}
                data-testid={`risk-row-${a.user_id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{a.name}</div>
              <div className="text-[10px] opacity-80">
                {a.completed}h done · expected {a.expected_by_now}h ·
                {a.scheduled_count > 0 ? ` ${a.scheduled_count} scheduled` : ' nothing scheduled'}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-semibold whitespace-nowrap">
                −{a.gap}h gap
              </div>
              <ChevronRight className="w-3 h-3 inline opacity-60" />
            </div>
          </Link>
        ))}
        {more > 0 && (
          <Link to="/hr" className="block text-center text-[11px] text-[#64748B] hover:underline pt-1">
            + {more} more at-risk aide{more === 1 ? '' : 's'} →
          </Link>
        )}
      </CardContent>
    </Card>
  );
}
